import { cn } from '@utazzo/ui';
import React from 'react';

import {
  ActiveSubBlock,
  barPct,
  FunnelStageCard,
  ninIN,
  PoolHeroCard,
  POOL_CARD,
  PoolMetricCard,
  SectionLabel,
  sharePct,
} from '../report-cards';
import { LegacyTable, ReportSection, type LegacyCell } from '../ReportSection';

import { CARRY_STATUS_FIELD, filterEntriesByRowData, splitDrillCell } from './band-helpers';

import type { BandProps } from './band-props';
import type { AgentTrackerRow, LeadListQuery } from '@utazzo/shared';

const POOL_STATUS_COLS: Array<{ code: string; label: string }> = [
  { code: 'not_qualified', label: 'Not Qualified' },
  { code: 'active', label: 'Active' },
  { code: 'won', label: 'Won' },
  { code: 'lost', label: 'Lost' },
];

const poolStatusCurr = (r: AgentTrackerRow, code: string): number => r.byStatus[code] ?? 0;
const poolStatusCf = (r: AgentTrackerRow, code: string): number => {
  const k = CARRY_STATUS_FIELD[code];
  return k ? (r.carriedIn[k] ?? 0) : 0;
};

/** Active-breakup sub-stages (current = bySubStatus, carry = carriedIn.current*). */
const ACTIVE_BREAKUP_COLS: Array<{
  code: string;
  carry: 'currentProposal' | 'currentWarm' | 'currentHot';
  label: string;
}> = [
  { code: 'proposal', carry: 'currentProposal', label: 'Proposal' },
  { code: 'warm', carry: 'currentWarm', label: 'Warm' },
  { code: 'hot', carry: 'currentHot', label: 'Hot' },
];

/**
 * The Lead pool band, split so its blocks land in the client dashboard flow
 * (pure relocation - no data/logic change). `variant="pool"` (position 4) =
 * the Lead Pool KPI cards + Agent-by-Lead-Pool table; `variant="active"`
 * (position 5) = the Active breakup card + the Active funnel. Both variants
 * read the SAME props.
 */
export const LeadPoolBand = ({
  variant,
  periodLabel,
  scopedRow,
  poolTotal,
  poolMonth,
  poolCarry,
  poolUnassigned,
  unassignedOpenCurrent,
  unassignedOpenCarry,
  activeSum,
  largeTotal,
  largeMonth,
  largeCarry,
  stTotal,
  stMonth,
  stCarry,
  sbTotal,
  sbMonth,
  sbCarryCur,
  sbMtd,
  sbCarryCum,
  navigate,
  openTotalPool,
  openCurrentPool,
  openCarryPool,
  openTotalStage,
  openCurrentStage,
  openCarryStage,
  openTotalSub,
  openCurrentSub,
  openCarrySub,
  openTotalLarge,
  openCurrentLarge,
  openCarryLarge,
  openTotalTerminal,
  report,
  totals,
  loading,
  agentEntries,
  agentCell,
  expandAllAction,
  drillCell,
}: BandProps & { variant: 'pool' | 'active' }): React.JSX.Element => {
  // Status / sub-status ids for the drill queries (by code, from the report).
  const statusIdByCode = (code: string): string | undefined =>
    report?.statuses.find((s) => s.code === code)?.id;
  const subStatusIdByCode = (code: string): string | undefined =>
    report?.subStatuses.find((s) => s.code === code)?.id;
  const activeId = statusIdByCode('active');
  const nqId = statusIdByCode('not_qualified');

  // Per-column drill query for the Agent-by-Lead-Pool table (combined pool).
  const poolColQuery = (code: string): Partial<LeadListQuery> => {
    switch (code) {
      case 'not_qualified':
        return nqId ? { currentStatusId: nqId } : {};
      case 'active':
        return activeId ? { currentStatusId: activeId, currentBucket: 'OPEN' } : {};
      case 'won':
        return { currentBucket: 'WON' };
      case 'lost':
        return { currentBucket: 'LOST' };
      default:
        return {};
    }
  };
  const poolRow = (r: AgentTrackerRow): LegacyCell[] => {
    const id = (r.agentId as string | null) ?? null;
    return [
      ...POOL_STATUS_COLS.map(({ code, label }) =>
        splitDrillCell(
          drillCell,
          poolStatusCurr(r, code),
          poolStatusCf(r, code),
          id,
          `${r.agentName} - ${label}`,
          poolColQuery(code),
        ),
      ),
      splitDrillCell(
        drillCell,
        r.totalLeads ?? 0,
        r.carriedInLeads ?? 0,
        id,
        `${r.agentName} - Pool`,
        {},
      ),
    ];
  };

  // Shared per-column drill query for the Active Breakup + Large Lead tables.
  // `large` adds the large-lead cohort flag to every cell.
  const breakupColQuery = (
    kind: 'proposal' | 'warm' | 'hot' | 'converted' | 'total',
    large: boolean,
  ): Partial<LeadListQuery> => {
    const base: Partial<LeadListQuery> = large ? { largeLeadQuery: true } : {};
    if (kind === 'converted') return { ...base, currentBucket: 'WON' };
    if (kind === 'total')
      return activeId ? { ...base, currentStatusId: activeId, currentBucket: 'OPEN' } : base;
    const sid = subStatusIdByCode(kind);
    return sid ? { ...base, currentSubStatusId: sid, currentBucket: 'OPEN' } : base;
  };

  /**
   * One "Agent by Active Lead Breakup" row: Proposal / Warm / Hot / Converted /
   * Total, each a current-month / carry-forward split. Current = this month's
   * sub-status counts (`bySubStatus`) + won (`byStatus.won`); carry-forward =
   * `carriedIn.current*`. Total is the sum of the four. Each cell drills to the
   * combined (curr + cf) pool for its stage.
   */
  const activeBreakupRow = (r: AgentTrackerRow): LegacyCell[] => {
    const id = (r.agentId as string | null) ?? null;
    const cells = ACTIVE_BREAKUP_COLS.map(({ code, carry, label }) =>
      splitDrillCell(
        drillCell,
        r.bySubStatus[code] ?? 0,
        r.carriedIn[carry] ?? 0,
        id,
        `${r.agentName} - ${label}`,
        breakupColQuery(code as 'proposal' | 'warm' | 'hot', false),
      ),
    );
    const convCurr = r.byStatus['won'] ?? 0;
    const convCf = r.carriedIn.currentWon ?? 0;
    const totalCurr =
      ACTIVE_BREAKUP_COLS.reduce((a, c) => a + (r.bySubStatus[c.code] ?? 0), 0) + convCurr;
    const totalCf =
      ACTIVE_BREAKUP_COLS.reduce((a, c) => a + (r.carriedIn[c.carry] ?? 0), 0) + convCf;
    return [
      ...cells,
      splitDrillCell(
        drillCell,
        convCurr,
        convCf,
        id,
        `${r.agentName} - Converted`,
        breakupColQuery('converted', false),
      ),
      splitDrillCell(
        drillCell,
        totalCurr,
        totalCf,
        id,
        `${r.agentName} - Active total`,
        breakupColQuery('total', false),
      ),
    ];
  };

  /** True when an agent has ANY large lead in any stage (this month or carry). */
  const hasLargeBreakup = (r: AgentTrackerRow): boolean => {
    const b = r.largeBreakup;
    return (
      b.proposalCur +
        b.warmCur +
        b.hotCur +
        b.wonCur +
        b.proposalCf +
        b.warmCf +
        b.hotCf +
        b.wonCf >
      0
    );
  };
  // Drop agents with zero large leads everywhere (noise), but keep a reporting
  // manager whose team has any so the hierarchy view stays intact.
  const largeEntries = filterEntriesByRowData(agentEntries, report, hasLargeBreakup);

  /** One "Large Lead" row: same columns as the active breakup, large leads only. */
  const largeBreakupRow = (r: AgentTrackerRow): LegacyCell[] => {
    const id = (r.agentId as string | null) ?? null;
    const b = r.largeBreakup;
    const curr = [b.proposalCur, b.warmCur, b.hotCur, b.wonCur];
    const cf = [b.proposalCf, b.warmCf, b.hotCf, b.wonCf];
    const kinds: Array<'proposal' | 'warm' | 'hot' | 'converted'> = [
      'proposal',
      'warm',
      'hot',
      'converted',
    ];
    const labels = ['Proposal', 'Warm', 'Hot', 'Converted'];
    return [
      ...kinds.map((kind, i) =>
        splitDrillCell(
          drillCell,
          curr[i] ?? 0,
          cf[i] ?? 0,
          id,
          `${r.agentName} - ${labels[i]} (large)`,
          breakupColQuery(kind, true),
        ),
      ),
      splitDrillCell(
        drillCell,
        curr.reduce((a, c) => a + c, 0),
        cf.reduce((a, c) => a + c, 0),
        id,
        `${r.agentName} - Large total`,
        breakupColQuery('total', true),
      ),
    ];
  };

  if (variant === 'active') {
    return (
      <div className="flex flex-col gap-[10px]">
        {/* Active breakup (full width). */}
        <div className={cn('gap-[10px]', POOL_CARD)}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-[12px] font-semibold whitespace-nowrap text-[#52525b]">
              Active breakup{' '}
              <span className="font-medium text-[#a1a1aa]">
                · Proposal + Warm + Hot = {ninIN(activeSum)}
              </span>
            </div>
            <div className="flex h-[6px] w-[160px] gap-[2px] overflow-hidden rounded-[3px]">
              <span
                className="bg-[#1d4ed8]"
                style={{ width: `${barPct(sbTotal('proposal'), activeSum)}%` }}
              />
              <span
                className="bg-[#1d4ed8] opacity-[0.65]"
                style={{ width: `${barPct(sbTotal('warm'), activeSum)}%` }}
              />
              <span className="flex-1 bg-[#1d4ed8] opacity-[0.35]" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-[12px]">
            <ActiveSubBlock
              label="Proposal"
              dotOpacity={1}
              value={sbTotal('proposal')}
              share={sharePct(sbTotal('proposal'), activeSum)}
              month={sbMonth('proposal')}
              carry={sbCarryCur('proposal')}
              onClick={() => openTotalSub('proposal')}
              onMonth={() => openCurrentSub('proposal')}
              onCarry={() => openCarrySub('proposal')}
            />
            <ActiveSubBlock
              label="Warm"
              dotOpacity={0.65}
              value={sbTotal('warm')}
              share={sharePct(sbTotal('warm'), activeSum)}
              month={sbMonth('warm')}
              carry={sbCarryCur('warm')}
              onClick={() => openTotalSub('warm')}
              onMonth={() => openCurrentSub('warm')}
              onCarry={() => openCarrySub('warm')}
            />
            <ActiveSubBlock
              label="Hot"
              dotOpacity={0.35}
              value={sbTotal('hot')}
              share={sharePct(sbTotal('hot'), activeSum)}
              month={sbMonth('hot')}
              carry={sbCarryCur('hot')}
              onClick={() => openTotalSub('hot')}
              onMonth={() => openCurrentSub('hot')}
              onCarry={() => openCarrySub('hot')}
            />
          </div>
        </div>

        {/* Active funnel: sitting now (Current) vs ever entered (MTD / Cum.). */}
        <div className="flex flex-col gap-[12px] rounded-xl border border-[#e4e4e7] bg-white px-[14px] py-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-[12px] font-semibold whitespace-nowrap text-[#52525b]">
              Active funnel · sitting now vs ever entered
            </div>
            <div className="flex flex-wrap gap-[14px] text-[11px] whitespace-nowrap text-[#71717a]">
              <span>
                <b className="font-bold text-[#094053]">Current</b> sitting in stage now
              </span>
              <span>
                <b className="font-bold text-[#094053]">MTD</b> entered this month
              </span>
              <span>
                <b className="font-bold text-[#094053]">Cum.</b> ever reached (carry cohort)
              </span>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-[10px] min-[561px]:grid-cols-2 min-[1001px]:grid-cols-4">
            <FunnelStageCard
              label="Active"
              dotOpacity={1}
              total={stTotal('active')}
              monthCur={stMonth('active')}
              monthMtd={scopedRow?.reachedActive ?? 0}
              carryCur={scopedRow?.carriedIn.currentActive ?? 0}
              carryCum={scopedRow?.carriedIn.reachedActive ?? 0}
            />
            <FunnelStageCard
              label="Proposal"
              dotOpacity={1}
              total={sbTotal('proposal')}
              monthCur={sbMonth('proposal')}
              monthMtd={sbMtd('proposal')}
              carryCur={sbCarryCur('proposal')}
              carryCum={sbCarryCum('proposal')}
            />
            <FunnelStageCard
              label="Warm"
              dotOpacity={0.6}
              total={sbTotal('warm')}
              monthCur={sbMonth('warm')}
              monthMtd={sbMtd('warm')}
              carryCur={sbCarryCur('warm')}
              carryCum={sbCarryCum('warm')}
            />
            <FunnelStageCard
              label="Hot"
              dotOpacity={0.3}
              total={sbTotal('hot')}
              monthCur={sbMonth('hot')}
              monthMtd={sbMtd('hot')}
              carryCur={sbCarryCur('hot')}
              carryCum={sbCarryCum('hot')}
            />
          </div>
        </div>

        <ReportSection title="Agent by Active Lead Breakup" actions={expandAllAction}>
          <LegacyTable
            loading={loading || !report}
            headerGroups={[
              { label: '', colSpan: 1 },
              { label: 'Total (Curr / CF)', colSpan: ACTIVE_BREAKUP_COLS.length + 2 },
            ]}
            headers={['Agent', ...ACTIVE_BREAKUP_COLS.map((c) => c.label), 'Converted', 'Total']}
            rows={agentEntries.map(({ row, ...entry }) => [
              { node: agentCell({ row, ...entry }), sort: row.agentName },
              ...activeBreakupRow(row),
            ])}
            totals={totals && report ? ['Total', ...activeBreakupRow(totals)] : undefined}
          />
        </ReportSection>

        <ReportSection title="Large Lead" actions={expandAllAction}>
          <LegacyTable
            loading={loading || !report}
            headerGroups={[
              { label: '', colSpan: 1 },
              { label: 'Total (Curr / CF)', colSpan: ACTIVE_BREAKUP_COLS.length + 2 },
            ]}
            headers={['Agent', ...ACTIVE_BREAKUP_COLS.map((c) => c.label), 'Converted', 'Total']}
            rows={largeEntries.map(({ row, ...entry }) => [
              { node: agentCell({ row, ...entry }), sort: row.agentName },
              ...largeBreakupRow(row),
            ])}
            totals={totals && report ? ['Total', ...largeBreakupRow(totals)] : undefined}
          />
        </ReportSection>
      </div>
    );
  }

  return (
    <section className="flex flex-col gap-[10px]">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <SectionLabel title="Lead pool" suffix={`${periodLabel} + carry forward`} />
        <div className="flex items-center gap-[14px] text-[11px] text-[#71717a]">
          <span className="flex items-center gap-[5px]">
            <span className="h-[5px] w-[14px] rounded-[3px] bg-[#094053]" />
            This month
          </span>
          <span className="flex items-center gap-[5px]">
            <span className="h-[5px] w-[14px] rounded-[3px] bg-[#094053] opacity-30" />
            Carry forward
          </span>
          <span className="flex items-center gap-[6px]">
            <span className="rounded-full bg-[#e3edf0] px-[7px] py-px text-[10px] font-bold text-[#094053]">
              %
            </span>
            share of pool · funnel of Active
          </span>
        </div>
      </div>

      {/* Row 1: Total hero + status snapshot. */}
      <div className="grid grid-cols-2 gap-[10px] min-[561px]:grid-cols-3 min-[1001px]:grid-cols-6">
        <PoolHeroCard
          total={poolTotal}
          month={poolMonth}
          carry={poolCarry}
          unassigned={poolUnassigned}
          onClick={openTotalPool}
          onMonth={openCurrentPool}
          onCarry={openCarryPool}
        />
        <PoolMetricCard
          label="Not qualified"
          dot="#b45309"
          value={stTotal('not_qualified')}
          numberColor="#b45309"
          barColor="#b45309"
          share={sharePct(stTotal('not_qualified'), poolTotal)}
          shareTitle="share of total pool"
          month={stMonth('not_qualified')}
          carry={stCarry('not_qualified')}
          onClick={() => openTotalStage('not_qualified')}
          onMonth={() => openCurrentStage('not_qualified')}
          onCarry={() => openCarryStage('not_qualified')}
        />
        <PoolMetricCard
          label="Active"
          dot="#1d4ed8"
          value={stTotal('active')}
          numberColor="#1d4ed8"
          barColor="#1d4ed8"
          share={sharePct(stTotal('active'), poolTotal)}
          shareTitle="share of total pool"
          month={stMonth('active')}
          carry={stCarry('active')}
          onClick={() => openTotalStage('active')}
          onMonth={() => openCurrentStage('active')}
          onCarry={() => openCarryStage('active')}
        />
        <PoolMetricCard
          label="Won"
          dot="#047857"
          value={stTotal('won')}
          numberColor="#047857"
          barColor="#047857"
          share={sharePct(stTotal('won'), poolTotal)}
          shareTitle="share of total pool"
          month={stMonth('won')}
          carry={stCarry('won')}
          onClick={() => openTotalTerminal('WON')}
        />
        <PoolMetricCard
          label="Lost"
          dot="#b91c1c"
          value={stTotal('lost')}
          numberColor="#b91c1c"
          barColor="#b91c1c"
          share={sharePct(stTotal('lost'), poolTotal)}
          shareTitle="share of total pool"
          month={stMonth('lost')}
          carry={stCarry('lost')}
          onClick={() => openTotalTerminal('LOST')}
        />
      </div>

      {/* Row 2: Large leads + Unassigned + Long follow-up. (The Active breakup
          card moved to the Active Lead Breakup section below.) */}
      <div className="grid grid-cols-2 gap-[10px] min-[561px]:grid-cols-3 min-[1001px]:grid-cols-6">
        <PoolMetricCard
          label="Large leads"
          dot="#b45309"
          value={largeTotal}
          numberColor="#094053"
          barColor="#b45309"
          share={sharePct(largeTotal, poolTotal)}
          shareTitle="share of total pool"
          month={largeMonth}
          carry={largeCarry}
          onClick={openTotalLarge}
          onMonth={openCurrentLarge}
          onCarry={openCarryLarge}
        />
        <PoolMetricCard
          label="Unassigned"
          dot="#b45309"
          value={poolUnassigned}
          numberColor="#094053"
          barColor="#b45309"
          share={sharePct(poolUnassigned, poolTotal)}
          shareTitle="share of total pool"
          month={unassignedOpenCurrent}
          carry={unassignedOpenCarry}
          onClick={() => navigate('/leads/exit-unassigned')}
        />
        <PoolMetricCard
          label="Long follow-up"
          dot="#71717a"
          value={stTotal('long_followup')}
          numberColor="#094053"
          barColor="#094053"
          share={sharePct(stTotal('long_followup'), poolTotal)}
          shareTitle="share of total pool"
          month={stMonth('long_followup')}
          carry={stCarry('long_followup')}
          onClick={() => openTotalStage('long_followup')}
          onMonth={() => openCurrentStage('long_followup')}
          onCarry={() => openCarryStage('long_followup')}
        />
      </div>

      <ReportSection title="Agent by Lead Pool" actions={expandAllAction}>
        <LegacyTable
          loading={loading || !report}
          headerGroups={[
            { label: '', colSpan: 1 },
            { label: 'Total (Curr / CF)', colSpan: POOL_STATUS_COLS.length + 1 },
          ]}
          headers={['Agent', ...POOL_STATUS_COLS.map((c) => c.label), 'Pool']}
          rows={agentEntries.map(({ row, ...entry }) => [
            { node: agentCell({ row, ...entry }), sort: row.agentName },
            ...poolRow(row),
          ])}
          totals={totals && report ? ['Total', ...poolRow(totals)] : undefined}
        />
      </ReportSection>
    </section>
  );
};
